import { ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { prospectAssignmentService } from '../../services/prospectService.js'
import { invalidateNavBadges } from '../ui/useNavBadges'
import { SWP_EMPLOYEE_ID } from '../../constants/swp.js'

/**
 * Composable for managing employee assignments to prospects
 * @param {Object} options
 * @param {Ref} options.prospectPreferences - Preferences cache ref
 * @param {Ref} options.prospectAssignments - Assignments cache ref
 * @param {Ref} options.approvedProspects - Computed ref of approved prospects
 * @param {Function} options.getPreferredEmployees - Function to parse preferred employees
 * @param {Function} options.reloadProspectData - Function to reload prospect data
 */
export function useSourcingAssignment({
  prospectPreferences,
  prospectAssignments,
  approvedProspects,
  getPreferredEmployees,
  reloadProspectData
}) {
  const queryClient = useQueryClient()

  // ==================== State ====================

  // Selected employees from preferred list: { 'prospectId-prefId': employeeSimplicateId }
  const selectedEmployees = ref({})

  // Custom selected employees (from modal): { 'prospectId-prefId': { id, name, dpw } }
  const customSelectedEmployees = ref({})

  // Assignment in progress state: { 'prospectId-prefId': boolean }
  const isAssigning = ref({})

  // ==================== Helper Functions ====================

  /**
   * Create selection key from prospect ID and preference
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @returns {string}
   */
  function getSelectionKey(prospectId, pref, prefIndex) {
    return `${prospectId}-${pref.id || prefIndex}`
  }

  /**
   * Check if assignment can be made
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @returns {boolean}
   */
  function canAssign(prospectId, pref, prefIndex) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    const selectedFromPreferred = selectedEmployees.value[key]
    const selectedCustom = customSelectedEmployees.value[key]
    return !!(selectedFromPreferred || selectedCustom)
  }

  /**
   * Check if a preference has an assignment
   * @param {number} prospectId
   * @param {Object} pref
   * @returns {boolean}
   */
  function hasAssignment(prospectId, pref) {
    return !!getAssignmentForPreference(prospectId, pref)
  }

  /**
   * Get assignment for a specific preference
   * @param {number} prospectId
   * @param {Object} pref
   * @returns {Object|undefined}
   */
  function getAssignmentForPreference(prospectId, pref) {
    const assignments = prospectAssignments.value[prospectId] || []

    // Primary: match via notes marker (used by new assignments)
    if (pref.id) {
      const byNotes = assignments.find(a => a.notes && a.notes.includes(`pref:${pref.id}`))
      if (byNotes) return byNotes
    }

    // Assignment met een pref-marker voor een ándere rol telt niet als fallback
    const belongsToOtherPref = a => {
      const m = a.notes?.match(/pref:([^\s|]+)/)
      return !!(m && pref.id !== undefined && String(m[1]) !== String(pref.id))
    }

    // Fallback 1: single preference → any legacy assignment matches
    const prefs = prospectPreferences.value[prospectId] || []
    if (prefs.length === 1 && assignments.length > 0 && !belongsToOtherPref(assignments[0])) {
      return assignments[0]
    }

    // Fallback 2: match via PL-selected employee (alleen legacy assignments)
    if (pref.pl_selected_employee_id) {
      const byPl = assignments.find(
        a => a.employee_simplicate_id === pref.pl_selected_employee_id && !belongsToOtherPref(a)
      )
      if (byPl) return byPl
    }

    return undefined
  }

  /**
   * Check if a prospect is fully assigned
   * @param {Object} prospect
   * @returns {boolean}
   */
  function isProspectFullyAssigned(prospect) {
    const prefs = prospectPreferences.value[prospect.id] || []
    if (prefs.length === 0) return false
    return prefs.every(pref => hasAssignment(prospect.id, pref))
  }

  /**
   * Get role count for a prospect
   * @param {Object} prospect
   * @returns {number}
   */
  function getRoleCount(prospect) {
    return prospectPreferences.value[prospect.id]?.length || 0
  }

  /**
   * Get assigned count for a prospect
   * @param {Object} prospect
   * @returns {number}
   */
  function getAssignedCount(prospect) {
    const prefs = prospectPreferences.value[prospect.id] || []
    return prefs.filter(pref => hasAssignment(prospect.id, pref)).length
  }

  /**
   * Get other proposals for an employee (excluding current prospect)
   * Used for conflict detection
   * @param {string} empId
   * @param {number} currentProspectId
   * @returns {Array}
   */
  function getOtherProposals(empId, currentProspectId) {
    // SWP has infinite availability — never report as conflict
    if (empId === SWP_EMPLOYEE_ID) return []

    const proposals = []

    for (const prospect of approvedProspects.value) {
      if (prospect.id === currentProspectId) continue

      const prefs = prospectPreferences.value[prospect.id] || []
      for (let prefIndex = 0; prefIndex < prefs.length; prefIndex++) {
        const pref = prefs[prefIndex]

        // Check if employee is actively SELECTED (radio button) for this preference
        // Definitief toegewezen medewerkers tellen niet mee als conflict
        const key = getSelectionKey(prospect.id, pref, prefIndex)
        const isSelectedHere = selectedEmployees.value[key] === empId

        if (isSelectedHere) {
          proposals.push({
            prospectId: prospect.id,
            organizationName: prospect.organization_name,
            subject: prospect.subject,
            role: pref.role || 'Medewerker',
            dpw: pref.dpw
          })
        }
      }
    }

    return proposals
  }

  // ==================== Selection Functions ====================

  /**
   * Select an employee from preferred list
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @param {string} employeeId
   */
  function selectEmployee(prospectId, pref, prefIndex, employeeId) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    selectedEmployees.value[key] = employeeId
    // Clear custom selection when selecting from preferred
    delete customSelectedEmployees.value[key]
  }

  /**
   * Set custom employee selection (from modal)
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @param {Object} employee - { id, name, dpw }
   */
  function setCustomEmployee(prospectId, pref, prefIndex, employee) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    customSelectedEmployees.value[key] = employee
    // Clear preferred selection when setting custom
    delete selectedEmployees.value[key]
  }

  /**
   * Clear custom employee selection
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   */
  function clearCustomEmployee(prospectId, pref, prefIndex) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    delete customSelectedEmployees.value[key]
  }

  /**
   * Get selected employee ID for a preference
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @returns {string|undefined}
   */
  function getSelectedEmployeeId(prospectId, pref, prefIndex) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    return selectedEmployees.value[key]
  }

  /**
   * Get custom selected employee for a preference
   * @param {number} prospectId
   * @param {Object} pref
   * @param {number} prefIndex
   * @returns {Object|undefined}
   */
  function getCustomSelectedEmployee(prospectId, pref, prefIndex) {
    const key = getSelectionKey(prospectId, pref, prefIndex)
    return customSelectedEmployees.value[key]
  }

  /**
   * Pre-select PL recommended employees for preferences
   * @param {number} prospectId
   * @param {Array} prefs
   */
  function preselectPlRecommended(prospectId, prefs) {
    for (let prefIndex = 0; prefIndex < prefs.length; prefIndex++) {
      const pref = prefs[prefIndex]
      const key = getSelectionKey(prospectId, pref, prefIndex)

      // Only pre-select if there's a PL recommendation and no existing assignment
      if (pref.pl_selected_employee_id && !hasAssignment(prospectId, pref)) {
        const preferredEmployees = getPreferredEmployees(pref)
        if (preferredEmployees.includes(pref.pl_selected_employee_id)) {
          selectedEmployees.value[key] = pref.pl_selected_employee_id
        }
      }
    }
  }

  // ==================== Assignment CRUD ====================

  /**
   * Create an assignment for a prospect
   * @param {Object} prospect
   * @param {Object} pref
   * @param {number} prefIndex
   * @returns {Promise<boolean>}
   */
  async function assignEmployee(prospect, pref, prefIndex) {
    const key = getSelectionKey(prospect.id, pref, prefIndex)
    const selectedFromPreferred = selectedEmployees.value[key]
    const selectedCustom = customSelectedEmployees.value[key]

    let employeeId, dpw

    if (selectedFromPreferred) {
      employeeId = selectedFromPreferred
      dpw = pref.dpw
    } else if (selectedCustom) {
      employeeId = selectedCustom.id
      dpw = selectedCustom.dpw
    } else {
      return false
    }

    isAssigning.value[key] = true

    // Create a marker to link assignment to preference
    const prefMarker = pref.id ? `pref:${pref.id}` : `pref:role:${pref.role || 'unknown'}`
    const notes = `${prefMarker} | Rol: ${pref.role || 'Medewerker'}`

    try {
      // Determine if preference has custom dates or should use project dates
      const hasCustomDates = !!(pref.start_date && pref.end_date)
      const usesProjectDates = !hasCustomDates
      const assignmentStartDate = pref.start_date || prospect.start_date
      const assignmentEndDate = pref.end_date || prospect.expected_closing_date

      await prospectAssignmentService.create({
        prospect_id: prospect.id,
        employee_simplicate_id: employeeId,
        dpw,
        start_date: assignmentStartDate,
        end_date: assignmentEndDate,
        notes,
        uses_project_dates: usesProjectDates
      })

      // Reload assignments
      await reloadProspectData(prospect.id)

      // Clear selections
      delete selectedEmployees.value[key]
      delete customSelectedEmployees.value[key]

      // Invalidate queries to refresh availability data
      queryClient.invalidateQueries({ queryKey: ['assign-availability'] })
      // Invalidate nav badges - concretiseren count may have decreased
      invalidateNavBadges(queryClient, { concretiseren: true })

      return true
    } catch (e) {
      console.error('Error creating assignment:', e)
      throw e
    } finally {
      isAssigning.value[key] = false
    }
  }

  /**
   * Remove an assignment
   * @param {Object} assignment
   * @returns {Promise<boolean>}
   */
  async function removeAssignment(assignment) {
    try {
      await prospectAssignmentService.delete(assignment.id)
      await reloadProspectData(assignment.prospect_id)
      queryClient.invalidateQueries({ queryKey: ['assign-availability'] })
      // Invalidate nav badges - concretiseren count may have increased
      invalidateNavBadges(queryClient, { concretiseren: true })
      return true
    } catch (e) {
      console.error('Error removing assignment:', e)
      throw e
    }
  }

  // ==================== Return ====================

  return {
    // State
    selectedEmployees,
    customSelectedEmployees,
    isAssigning,

    // Helper functions
    getSelectionKey,
    canAssign,
    hasAssignment,
    getAssignmentForPreference,
    isProspectFullyAssigned,
    getRoleCount,
    getAssignedCount,
    getOtherProposals,
    // Selection functions
    selectEmployee,
    setCustomEmployee,
    clearCustomEmployee,
    getSelectedEmployeeId,
    getCustomSelectedEmployee,
    preselectPlRecommended,

    // Assignment CRUD
    assignEmployee,
    removeAssignment
  }
}
